import React, { useState } from 'react';
import { ChangePasswordCredentials, authService } from '../services/authService';

const ChangePassword: React.FC = () => {
  const [credentials, setCredentials] = useState<ChangePasswordCredentials>({
    currentPassword: '',
    newPassword: '',
  });
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const [passwordStrengthText, setPasswordStrengthText] = useState<string>('');
  const [passwordStrengthColor, setPasswordStrengthColor] = useState<string>('');

  const calculatePasswordStrength = (password: string): { strength: number; text: string; color: string } => {
    if (!password) return { strength: 0, text: '', color: '' };
    
    let strength = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      veryLong: password.length >= 12
    };
    
    Object.values(checks).forEach(check => {
      if (check) strength++;
    });
    
    if (strength <= 2) return { strength, text: 'Zayıf', color: '#dc2626' };
    if (strength <= 4) return { strength, text: 'Orta', color: '#f59e0b' };
    return { strength, text: 'Güçlü', color: '#10b981' };
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else if (name === 'newPassword') {
      setCredentials(prev => ({
        ...prev,
        [name]: value,
      }));
      // Calculate password strength
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength.strength);
      setPasswordStrengthText(strength.text);
      setPasswordStrengthColor(strength.color);
    } else {
      setCredentials(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    setError('');
    setSuccess('');
  };

  const validateForm = (): boolean => {
    if (!credentials.currentPassword || !credentials.newPassword || !confirmPassword) {
      setError('Tüm alanlar doldurulmalıdır');
      return false;
    }

    if (credentials.newPassword.length < 6) {
      setError('Yeni şifre en az 6 karakter olmalıdır');
      return false;
    }

    if (credentials.newPassword !== confirmPassword) {
      setError('Yeni şifreler eşleşmiyor');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await authService.changePassword(credentials);
      setSuccess('Şifreniz başarıyla değiştirildi!');
      
      // Formu temizle
      setCredentials({
        currentPassword: '',
        newPassword: '',
      });
      setConfirmPassword('');
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Şifre değiştirme başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-container">
      <div className="change-password-form">
        <h2>🔐 Şifre Değiştir</h2>
        <p className="form-description">
          Hesabınızın güvenliği için şifrenizi düzenli olarak değiştirmeniz önerilir.
        </p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="currentPassword">Mevcut Şifre</label>
            <div className="password-input-container">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={credentials.currentPassword}
                onChange={handleChange}
                required
                placeholder="Mevcut şifrenizi girin"
                className="form-input"
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                aria-label={showCurrentPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {showCurrentPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">Yeni Şifre</label>
            <div className="password-input-container">
              <input
                type={showNewPassword ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={credentials.newPassword}
                onChange={handleChange}
                required
                placeholder="Yeni şifrenizi girin (en az 6 karakter)"
                className="form-input"
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {showNewPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
            {passwordStrength > 0 && (
              <div className="password-strength-indicator">
                <div className="strength-bar">
                  <div 
                    className="strength-fill"
                    style={{ 
                      width: `${(passwordStrength / 6) * 100}%`,
                      backgroundColor: passwordStrengthColor
                    }}
                  />
                </div>
                <span className="strength-text" style={{ color: passwordStrengthColor }}>
                  Şifre gücü: {passwordStrengthText}
                </span>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Yeni Şifre (Tekrar)</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={handleChange}
                required
                placeholder="Yeni şifrenizi tekrar girin"
                className="form-input"
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {showConfirmPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <button 
            type="submit" 
            disabled={loading} 
            className="change-password-button"
          >
            {loading ? 'Şifre Değiştiriliyor...' : 'Şifre Değiştir'}
          </button>
        </form>

        <div className="password-tips">
          <h3>🛡️ Güçlü Şifre İpuçları</h3>
          <ul>
            <li>En az 6 karakter kullanın</li>
            <li>Büyük ve küçük harfler karışık kullanın</li>
            <li>Sayılar ve semboller ekleyin</li>
            <li>Kişisel bilgiler kullanmayın</li>
            <li>Aynı şifreyi birden fazla yerde kullanmayın</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
